import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Clock, User, Calendar, Edit, List, Trash2, X, Settings, FileText, Info } from 'lucide-react';
import axios from 'axios';
import { updateJob, deleteJob, refreshJobs, refreshArchiveJobs, updateJobInArray, removeJobById } from '../../redux/features/jobSlice';
import DeadlineMarker from '../job/DeadlineMarker';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { toast } from 'react-toastify';
import JobForm from '../forms/JobForm';
import JobQueueExpandedDetails from './JobQueueExpandedDetails';
import QueueSearchFilters from './QueueSearchFilters';
import { API_BASE_URL } from '../../config/api.js';
import { getWorkerName, getWorkerColor, getWorkerInitials, getAssigneeColor } from '../../utils/workerUtils';
import {
  resolveOutgoingHandoffPartnerAssignee,
  isOutgoingHandoffEditLocked
} from '../../utils/handoffSettlementUtils';
import { getJobDisplayStatus, formatDisplayValue } from '../../utils/displayUtils';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { filterJobsForTenant, shouldApplyJobSocketPayload } from '../../utils/tenantUtils';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { fetchDeviceCategories } from '../../redux/api';
import {
  getHandoffWorkflowBadge,
  isOutgoingHandoffSourceView,
  isOutgoingHandoffJob,
  isOutgoingHandoffActiveForSender,
  resolveHandoffWorkflowPhase,
  sortOutgoingHandoffJobs
} from '../../utils/handoffWorkflowUtils';
import {
  EMPTY_QUEUE_FILTERS,
  jobMatchesSearch,
  jobMatchesQueueCriteria
} from '../../utils/queueFilterUtils';

const JobQueue = ({ jobs = [], loading = false, onJobSelect, selectedJobId, showStats = true, onJobSchedule, showAllJobs = false, variant = 'unscheduled' }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [openSections, setOpenSections] = useState({}); // Track which section is open for each job
  const [selectedJob, setSelectedJob] = useState(null);
  const selectedJobRef = useRef(null);
  const lastWorkerJobsRefreshRef = useRef(0);
  const [imagePreviewJob, setImagePreviewJob] = useState(null);

  useEffect(() => {
    selectedJobRef.current = selectedJob;
  }, [selectedJob]);
  const { user, userType } = useSelector((state) => state.auth);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { socket, isConnected } = useWebSocket();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  
  // Filter states - now arrays to support multiple selections
  const [filters, setFilters] = useState({ ...EMPTY_QUEUE_FILTERS });
  const [deviceCategories, setDeviceCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [outgoingHandoffFilter, setOutgoingHandoffFilter] = useState('all');
  const [outgoingSearchTerm, setOutgoingSearchTerm] = useState('');
  const [outgoingFilters, setOutgoingFilters] = useState({ ...EMPTY_QUEUE_FILTERS });
  const [showOutgoingFilters, setShowOutgoingFilters] = useState(false);

  // Efektivan korisnik (Redux + fallback na localStorage) – bitno za radnički login
  const effectiveUser = useMemo(() => {
    if (user && user.result) return user;
    if (typeof window !== 'undefined') {
      try {
        const profileStr = localStorage.getItem('profile');
        if (profileStr) {
          const profile = JSON.parse(profileStr);
          return profile;
        }
      } catch (e) {
        console.error('JobQueue: error reading profile from localStorage', e);
      }
    }
    return user;
  }, [user]);

  // Efektivan tip korisnika (čita i iz Redux-a i iz localStorage-a)
  const effectiveUserType = useMemo(() => {
    // First check localStorage (source of truth, especially when switching users)
    if (typeof window !== 'undefined') {
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType) {
        return storedUserType;
      }
    }
    
    // Fallback to Redux userType
    if (userType) {
      return userType;
    }
    
    // Last resort: check user structure to determine type
    // Worker has firstName/lastName but no companyName
    // Company has companyName
    const userResult = effectiveUser?.result;
    if (userResult) {
      const hasWorkerStructure = userResult.firstName && userResult.lastName && !userResult.companyName;
      const hasCompanyStructure = userResult.companyName;
      
      if (hasWorkerStructure && !hasCompanyStructure) {
        return 'worker';
      }
      if (hasCompanyStructure) {
        return 'company';
      }
    }
    
    return 'company';
  }, [userType, effectiveUser]);

  // Dozvole radnika za uređivanje / brisanje poslova
  const workerPermissions = useMemo(() => {
    if (typeof window === 'undefined') return {};
    try {
      const permsStr = localStorage.getItem('workerPermissions') || '{}';
      return JSON.parse(permsStr);
    } catch (e) {
      console.error('Error parsing workerPermissions:', e);
      return {};
    }
  }, []);

  const canEditJobs = effectiveUserType === 'company' || (effectiveUserType === 'worker' && !!workerPermissions.canEditJobs);
  const canDeleteJobs = effectiveUserType === 'company' || (effectiveUserType === 'worker' && !!workerPermissions.canDeleteJobs);

  const currentCompanyId = useMemo(() => {
    const r = effectiveUser?.result;
    if (!r) return null;
    if (effectiveUserType === 'company') {
      const id = r._id ?? r.id;
      return id != null && String(id).trim() !== '' ? String(id).trim() : null;
    }
    if (effectiveUserType === 'worker') {
      const cid = r.companyId ?? r.company?._id ?? r.company?.id;
      return cid != null && String(cid).trim() !== '' ? String(cid).trim() : null;
    }
    return null;
  }, [effectiveUser, effectiveUserType]);

  const handoffRowPrimaryLabel = useCallback((job) => {
    const addrFmt = formatDisplayValue(job?.clientAddress);
    if (addrFmt && addrFmt !== '-') return addrFmt;
    return formatDisplayValue(job?.clientName);
  }, []);
  
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(getWorkers());
    // Silently handle partner fetch errors - endpoint may not exist
    dispatch(getPartners()).catch(() => {
      // Partner endpoint may not be available, fail silently
    });
  }, [dispatch]);

  // Fetch device categories
  useEffect(() => {
    const loadDeviceCategories = async () => {
      try {
        const response = await fetchDeviceCategories();
        if (response?.data) {
          setDeviceCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching device categories:', error);
        setDeviceCategories([]);
      }
    };
    loadDeviceCategories();
  }, []);

  // WebSocket: ne pozivaj getJobs na svaki timer tick (radnik šalje ~2s + PATCH emituje pun job → lavina GET /api/jobs).
  useEffect(() => {
    if (!socket || !isConnected) return undefined;

    const businessType = getBusinessType();

    const applyJobUpdated = (data) => {
      if (!shouldApplyJobSocketPayload(data)) return;

      const sel = selectedJobRef.current;
      const terminal =
        data.status === 'Completed' ||
        data.status === 'Finished' ||
        data.status === 'Archived' ||
        data.job?.status === 'Completed' ||
        data.job?.status === 'Finished' ||
        data.job?.status === 'Archived';

      if (terminal && sel && data.jobId && String(data.jobId) === String(sel._id)) {
        setSelectedJob(null);
      }

      if (data.job && data.job._id) {
        dispatch(updateJobInArray({ jobId: data.job._id, updates: data.job, fullJob: data.job }));
      } else if (data.jobId) {
        const updates = {};
        if (data.status !== undefined) updates.status = data.status;
        if (data.travelStartTime !== undefined) updates.travelStartTime = data.travelStartTime;
        if (data.workStartTime !== undefined) updates.workStartTime = data.workStartTime;
        if (data.accumulatedTravelTime !== undefined) updates.accumulatedTravelTime = data.accumulatedTravelTime;
        if (data.accumulatedWorkTime !== undefined) updates.accumulatedWorkTime = data.accumulatedWorkTime;
        if (Object.keys(updates).length > 0) {
          dispatch(updateJobInArray({ jobId: data.jobId, updates }));
        }
      }

      // Ne zovi GET /api/jobs ovde: CalendarWithDragDrop sluša isti socket i duplira zahtev po događaju.
      // Terminal stanje već ažuriramo preko updateJobInArray iznad.
    };

    socket.on('job_updated', (data) => {
      applyJobUpdated(data);
    });

    socket.on('job_deleted', (data) => {
      if (data?.jobId) dispatch(removeJobById(data.jobId));
      const sel = selectedJobRef.current;
      if (sel && data.jobId === sel._id) setSelectedJob(null);
    });

    socket.on('worker_status_changed', () => {
      if (!businessType) return;
      const now = Date.now();
      if (now - lastWorkerJobsRefreshRef.current < 15000) return;
      lastWorkerJobsRefreshRef.current = now;
      dispatch(refreshJobs());
    });

    return () => {
      socket.off('job_updated');
      socket.off('job_deleted');
      socket.off('worker_status_changed');
    };
  }, [socket, isConnected, dispatch]);

  // Jobs are passed as props from parent component

  // Toggle expanded state for job details - only one can be open at a time
  const toggleExpanded = (jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      // Clear open sections when closing job details
      setOpenSections(prev => {
        const newState = { ...prev };
        delete newState[jobId];
        return newState;
      });
    } else {
      setExpandedJobId(jobId);
      // Initialize with no sections open
      setOpenSections(prev => ({ ...prev, [jobId]: null }));
    }
  };

  // Toggle section within job details - only one section open at a time per job
  const toggleSection = (jobId, sectionKey) => {
    setOpenSections(prev => {
      const currentOpen = prev[jobId];
      // If clicking the same section, close it. Otherwise, open the clicked section.
      return {
        ...prev,
        [jobId]: currentOpen === sectionKey ? null : sectionKey
      };
    });
  };

  // Handle job click - open details modal
  const handleJobClick = (job) => {
    // If onJobSelect is provided (WorkerDashboard), use that instead of local modal
    if (onJobSelect) {
      onJobSelect(job);
    } else {
      // Only open local modal if no onJobSelect callback (AdminDashboard)
      setSelectedJob(job);
    }
  };

  // Handle job delete with confirmation
  const handleDeleteJob = async (job) => {
    if (window.confirm(`${t('jobs.deleteConfirmation')} "${job.clientName}"?`)) {
      try {
        await dispatch(deleteJob(job._id)).unwrap();
        dispatch(refreshJobs());
        toast.success(t('common.success'));
      } catch (error) {
        toast.error(error.message || 'Greška pri brisanju posla');
      }
    }
  };

  const queueMatchOptions = useMemo(
    () => ({
      workers,
      partners,
      deviceCategories,
      currentCompanyId
    }),
    [workers, partners, deviceCategories, currentCompanyId]
  );

  const filteredJobs = useMemo(() => {
    return (jobs || []).filter((job) => {
      if (isOutgoingHandoffJob(job, currentCompanyId)) return false;
      const isUnscheduled = showAllJobs ? true : !job.serviceDate;
      if (!(isUnscheduled && job.status !== 'Completed')) return false;
      if (!jobMatchesSearch(job, searchTerm)) return false;
      return jobMatchesQueueCriteria(job, filters, { ...queueMatchOptions, mode: 'worker' });
    });
  }, [jobs, searchTerm, filters, showAllJobs, currentCompanyId, queueMatchOptions]);

  const getOutgoingHandoffFilterKey = useCallback((job) => {
    if (!job) return 'other';
    if (job.handoffDeletedByPartner) return 'deleted';
    if (job.handoffIsReturn) return 'returned';
    const phase = resolveHandoffWorkflowPhase(job, currentCompanyId);
    switch (phase) {
      case 'forwarded':
        return 'forwarded';
      case 'accepted':
        return 'accepted';
      case 'scheduled':
        return 'scheduled';
      case 'new_visit':
        return 'new_visit';
      case 'ready_to_close':
        return 'completed';
      case 'closed':
        return 'closed';
      case 'deleted_by_partner':
        return 'deleted';
      default:
        return 'other';
    }
  }, [currentCompanyId]);

  /** Poslovi koje smo mi poslali partneru — bez filtera pretrage/statusa/zakazivanja. */
  const outgoingHandoffJobs = useMemo(
    () =>
      sortOutgoingHandoffJobs(
        (jobs || []).filter((job) => isOutgoingHandoffActiveForSender(job, currentCompanyId))
      ),
    [jobs, currentCompanyId]
  );

  const filteredOutgoingHandoffJobs = useMemo(
    () =>
      outgoingHandoffJobs.filter(
        (job) =>
          jobMatchesSearch(job, outgoingSearchTerm) &&
          jobMatchesQueueCriteria(job, outgoingFilters, { ...queueMatchOptions, mode: 'partner' })
      ),
    [outgoingHandoffJobs, outgoingSearchTerm, outgoingFilters, queueMatchOptions]
  );

  const outgoingHandoffFilterCounts = useMemo(() => {
    const counts = {
      all: filteredOutgoingHandoffJobs.length,
      forwarded: 0,
      accepted: 0,
      scheduled: 0,
      new_visit: 0,
      completed: 0,
      returned: 0,
      deleted: 0,
      closed: 0
    };
    filteredOutgoingHandoffJobs.forEach((job) => {
      const key = getOutgoingHandoffFilterKey(job);
      if (counts[key] != null) counts[key] += 1;
    });
    return counts;
  }, [filteredOutgoingHandoffJobs, getOutgoingHandoffFilterKey]);

  const outgoingHandoffFilterButtons = useMemo(() => {
    const always = [
      { key: 'all', label: 'Svi poslovi', className: 'bg-slate-600' },
      { key: 'forwarded', label: 'Čeka prijem', className: 'bg-red-600' },
      { key: 'accepted', label: 'Prihvaćeni', className: 'bg-orange-500' },
      { key: 'scheduled', label: 'Zakazani', className: 'bg-blue-600' },
      { key: 'new_visit', label: 'Nova poseta', className: 'bg-purple-600' },
      { key: 'completed', label: 'Završeni', className: 'bg-green-600' }
    ];
    const optional = [
      { key: 'returned', label: 'Vraćeni', className: 'bg-rose-600' },
      { key: 'deleted', label: 'Obrisani', className: 'bg-gray-600' },
      { key: 'closed', label: 'Zatvoreni', className: 'bg-slate-500' }
    ].filter((item) => outgoingHandoffFilterCounts[item.key] > 0);
    return [...always, ...optional];
  }, [outgoingHandoffFilterCounts]);

  const visibleOutgoingHandoffJobs = useMemo(() => {
    if (outgoingHandoffFilter === 'all') return filteredOutgoingHandoffJobs;
    return filteredOutgoingHandoffJobs.filter(
      (job) => getOutgoingHandoffFilterKey(job) === outgoingHandoffFilter
    );
  }, [filteredOutgoingHandoffJobs, outgoingHandoffFilter, getOutgoingHandoffFilterKey]);

  const workerFilterItems = useMemo(
    () =>
      (workers || []).map((worker) => ({
        id: String(worker._id || worker.id),
        label: `${worker.firstName || ''} ${worker.lastName || ''}`.trim() || String(worker._id)
      })),
    [workers]
  );

  const partnerFilterItems = useMemo(
    () =>
      (partners || []).map((partner) => ({
        id: String(partner._id || partner.id),
        label: partner.name || partner.companyName || String(partner._id || partner.id)
      })),
    [partners]
  );

  const regularUnscheduledJobs = useMemo(
    () =>
      (filteredJobs || []).filter(
        (job) => !isOutgoingHandoffJob(job, currentCompanyId)
      ),
    [filteredJobs, currentCompanyId]
  );

  const getHandoffBorderClass = (job) => {
    const phase = resolveHandoffWorkflowPhase(job, currentCompanyId);
    switch (phase) {
      case 'deleted_by_partner':
        return 'border-gray-500';
      case 'closed':
        return 'border-slate-500';
      case 'forwarded':
        return 'border-red-500';
      case 'accepted':
        return 'border-orange-500';
      case 'scheduled':
        return 'border-blue-500';
      case 'new_visit':
        return 'border-purple-500';
      case 'ready_to_close':
        return 'border-green-500';
      default:
        return 'border-amber-500';
    }
  };

  const handlePartnerCloseJob = async (job, e) => {
    e?.stopPropagation();
    try {
      const updated = await dispatch(
        updateJob({
          id: job._id,
          jobData: { handoffPartnerClosed: true, handoffWorkflowPhase: 'closed' }
        })
      ).unwrap();
      toast.success('Stranka spremna za arhivu');
      handleJobClick(updated || job);
    } catch (error) {
      toast.error(error?.message || 'Greška pri završavanju stranke');
    }
  };

  const renderHandoffPhaseBadge = (job) => {
    const badge = getHandoffWorkflowBadge(job, currentCompanyId);
    if (!badge) return null;

    if (badge.isAction) {
      return (
        <button
          type="button"
          onClick={(e) => handlePartnerCloseJob(job, e)}
          className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded whitespace-nowrap text-white font-semibold max-w-[140px] truncate ${badge.className} hover:opacity-90`}
          title="Završi stranku i otvori štampu / arhivu"
        >
          {badge.label}
        </button>
      );
    }

    return (
      <span
        className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded whitespace-nowrap text-white max-w-[140px] truncate ${badge.className}`}
        title={badge.title || undefined}
      >
        {badge.label}
      </span>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'pending': return 'bg-yellow-500';
      case 'scheduled': return 'bg-blue-500';
      case 'in_progress': return 'bg-orange-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-500';
      case 'high': return 'text-orange-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('sr-RS');
  };

  const formatDuration = (duration) => {
    if (!duration) return null;
    if (duration === 0.5) return '30min';
    if (duration === 1) return '1h';
    if (duration % 1 === 0) return `${duration}h`;
    return `${duration}h`;
  };

  // Helper function to filter out placeholder/default values
  const filterPlaceholder = (value) => {
    if (!value) return '';
    const normalizedValue = String(value).trim();
    const placeholderValues = [
      t('common.unknownClient'),
      t('common.unknownDevice'),
      t('common.unknownBrand'),
      t('common.unknownModel'),
      t('common.noDescription'),
      t('common.notAvailable'),
      t('common.notAvailable').toLowerCase(),
      'No phone',
      'Unassigned',
      t('common.unknownClient').toLowerCase(),
      t('common.unknownDevice').toLowerCase(),
      t('common.unknownBrand').toLowerCase(),
      t('common.unknownModel').toLowerCase(),
      t('common.noDescription').toLowerCase(),
      'no phone',
      'unassigned',
      // Keep old English values for backward compatibility
      'Unknown Client',
      'Unknown Device',
      'Unknown Brand',
      'Unknown Model',
      'No description provided',
      'N/A',
      'n/a'
    ];
    return placeholderValues.includes(normalizedValue) ? '' : normalizedValue;
  };

  // Get display status: use the utility function that handles pause detection and translation
  const getDisplayStatus = (job) => {
    return getJobDisplayStatus(job);
  };
  const hasRealUpdate = (job) => {
    if (!job?.createdAt || !job?.updatedAt) return false;
    const createdMs = new Date(job.createdAt).getTime();
    const updatedMs = new Date(job.updatedAt).getTime();
    if (!Number.isFinite(createdMs) || !Number.isFinite(updatedMs)) return false;
    if (updatedMs - createdMs > 1000) return true;
    if (Array.isArray(job.activityLog)) {
      return job.activityLog.some((entry) => entry?.action && entry.action !== 'created');
    }
    return false;
  };

  const handleHandoffArchived = useCallback((jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
    }
    dispatch(refreshJobs());
    dispatch(refreshArchiveJobs());
  }, [dispatch, expandedJobId]);

  const expandedDetailsProps = {
    openSections,
    onToggleSection: toggleSection,
    onPreviewImages: setImagePreviewJob,
    t,
    workers,
    partners,
    currentCompanyId,
    formatDate,
    formatDuration,
    filterPlaceholder,
    getDisplayStatus,
    hasRealUpdate,
    onHandoffArchived: handleHandoffArchived
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const isForwardedVariant = variant === 'forwarded';
  const cardAccent = isForwardedVariant
    ? 'border-t-2 border-t-rose-500/80'
    : 'border-t-2 border-t-amber-500/80';

  return (
    <div className={`bg-gray-800/90 rounded-xl p-2 sm:p-4 flex flex-col relative border border-white/5 shadow-lg shadow-black/10 ${cardAccent}`}>
      {!isForwardedVariant && (
      <div className="mb-1">
        <QueueSearchFilters
          t={t}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t('jobs.searchJobs')}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((prev) => !prev)}
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={() => setFilters({ ...EMPTY_QUEUE_FILTERS })}
          peopleKind="worker"
          peopleItems={workerFilterItems}
          deviceCategories={deviceCategories}
          showUnassigned
          layout="toolbar"
          accent="amber"
          headerLeft={
            <h2 className="text-sm sm:text-base font-semibold text-white tracking-tight">
              {t('jobs.jobQueue')}
            </h2>
          }
        />
      </div>
      )}

      {isForwardedVariant && (
      <div className="mb-1">
        <QueueSearchFilters
          t={t}
          searchTerm={outgoingSearchTerm}
          onSearchChange={setOutgoingSearchTerm}
          searchPlaceholder={t('jobs.searchForwardedJobs')}
          showFilters={showOutgoingFilters}
          onToggleFilters={() => setShowOutgoingFilters((prev) => !prev)}
          filters={outgoingFilters}
          onFiltersChange={setOutgoingFilters}
          onClearFilters={() => setOutgoingFilters({ ...EMPTY_QUEUE_FILTERS })}
          peopleKind="partner"
          peopleItems={partnerFilterItems}
          deviceCategories={deviceCategories}
          layout="toolbar"
          accent="rose"
          headerLeft={
            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-rose-200 tracking-tight">
                {t('jobs.forwardedJobs')}
              </h2>
              {outgoingHandoffFilterButtons.map((item) => {
                const count = outgoingHandoffFilterCounts[item.key] || 0;
                const isActive = outgoingHandoffFilter === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOutgoingHandoffFilter(item.key);
                    }}
                    className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded-sm whitespace-nowrap font-semibold ${
                      isActive
                        ? `${item.className} text-white`
                        : 'bg-gray-700/80 text-gray-300 hover:bg-gray-600'
                    }`}
                    title={`${item.label} (${count})`}
                  >
                    {item.label} {count}
                  </button>
                );
              })}
            </div>
          }
        />
      </div>
      )}

      {!isForwardedVariant && !loading && (() => {
        const relevantUnscheduled = (jobs || []).filter((job) => {
          if (isOutgoingHandoffJob(job, currentCompanyId)) return false;
          const isUnscheduled = showAllJobs ? true : !job.serviceDate;
          return isUnscheduled && job.status !== 'Completed';
        });
        const foundCount = regularUnscheduledJobs.length;

        return (
          <div className="mb-2 text-xs sm:text-sm text-gray-400">
            {foundCount === 0 ? (
              <span>{t('jobs.noJobsMatch')}</span>
            ) : foundCount === 0 ? null : (
              <span>
                {foundCount} {foundCount === 1 ? t('jobs.jobFound') : t('jobs.jobsFound')}
                {relevantUnscheduled.length !== foundCount && (
                  <span className="text-gray-500 ml-1">
                    ({t('jobs.outOf')} {relevantUnscheduled.length})
                  </span>
                )}
              </span>
            )}
          </div>
        );
      })()}

      {/* Jobs List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-400">Loading jobs...</p>
        </div>
      ) : (
        // Droppable requires DragDropContext parent
        // If DragDropContext is not available, this will cause an error
        // We'll wrap it in ErrorBoundary in parent component
        <Droppable droppableId={isForwardedVariant ? 'job-queue-forwarded' : 'job-queue'} isDropDisabled={true}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`space-y-2 w-full ${
                  isMobile ? 'max-h-[400px] overflow-y-auto' : ''
                } ${snapshot.isDraggingOver ? 'bg-gray-700' : ''}`}
              >
              {!isForwardedVariant && regularUnscheduledJobs.map((job, index) => (
                <Draggable key={job._id} draggableId={job._id} index={index} isDragDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={() => handleJobClick(job)}
                      className={`bg-gray-700 rounded-lg border-l-4 cursor-pointer ${
                        selectedJobId === job._id ? 'border-blue-500 bg-gray-600' : 'border-gray-600'
                      } ${(job.isReturnVisit || job.lastVisitDate) ? 'border-purple-500' : ''} ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                    >
                      {/* Ultra Compact Job Row - All Horizontal */}
                      <div className="p-1.5 sm:p-2">
                        <div className="flex items-center justify-between gap-1.5 sm:gap-2">
                          {/* All info in one horizontal line */}
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                            {/* Status Badge */}
                            <span className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded whitespace-nowrap ${getStatusColor(job.status)}`}>
                              {job.status.substring(0, 3)}
                            </span>

                            {/* Priority Badge */}
                            <span className={`text-[9px] sm:text-[10px] font-medium whitespace-nowrap ${getPriorityColor(job.priority)}`}>
                              {job.priority.substring(0, 1)}
                            </span>
                            
                            {/* Return Visit Badge - Show if isReturnVisit is true or lastVisitDate exists */}
                            {(job.isReturnVisit || job.lastVisitDate) && (
                              <span 
                                className="px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded whitespace-nowrap bg-purple-500 text-white font-bold" 
                                title={`Već posećeno: ${job.lastVisitDate ? formatDate(job.lastVisitDate) : 'Nema datuma'}`}
                              >
                                ↻
                              </span>
                            )}
                            
                            {/* Adresa (ili klijent) kao glavni prikaz */}
                            <span className="text-[10px] sm:text-xs font-medium text-white truncate flex-1 min-w-0">
                              {handoffRowPrimaryLabel(job)}
                            </span>

                            <span onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0 ml-auto">
                              {filterPlaceholder(job.assignedTo) && (
                                <span
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                                  style={{ backgroundColor: getWorkerColor(job.assignedTo, workers), background: getWorkerColor(job.assignedTo, workers) }}
                                  title={getWorkerName(job.assignedTo, workers, partners)}
                                >
                                  {getWorkerInitials(job.assignedTo, workers, partners)}
                                </span>
                              )}
                              <DeadlineMarker job={job} size="sm" />
                            </span>
                            
                            {/* Client Name (secondary, after address) */}
                            <span className="hidden sm:inline text-[9px] sm:text-[10px] text-gray-300 truncate max-w-[120px]">
                              {formatDisplayValue(job.clientName)}
                            </span>
                            
                            {/* Date and Time */}
                            {job.serviceDate && (
                              <div className="flex items-center gap-0.5 text-[9px] sm:text-[10px] text-gray-400 whitespace-nowrap">
                                <Calendar className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                                <span>{formatDate(job.serviceDate).split(',')[0]}</span>
                                {job.scheduledTime && (
                                  <span className="ml-0.5">@ {job.scheduledTime}</span>
                                )}
                                {job.estimatedDuration && (
                                  <span className="ml-0.5 text-blue-400">({formatDuration(job.estimatedDuration)})</span>
                                )}
                              </div>
                            )}
                            
                            {/* Last Visit Date - Show if it's a return visit */}
                            {(job.isReturnVisit || job.lastVisitDate) && job.lastVisitDate && (
                              <div className="flex items-center gap-0.5 text-[9px] sm:text-[10px] text-purple-400 whitespace-nowrap font-medium" title={`Datum posete: ${formatDate(job.lastVisitDate)}`}>
                                <Clock className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                                <span>{formatDate(job.lastVisitDate).split(',')[0]}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Action Buttons - List, Edit, Delete */}
                          <div className="flex items-center gap-0.5 sm:gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(job._id);
                              }}
                              className="p-0.5 rounded hover:bg-gray-600 text-gray-300 hover:text-white"
                              title={t('common.details')}
                            >
                              <List className="h-3 w-3 stroke-[2.5]" />
                            </button>
                            
                            {/* Edit/Delete — company users or workers with matching permissions */}
                            {(canEditJobs || canDeleteJobs) && (
                              <>
                                {canEditJobs && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingJob(job);
                                  }}
                                  className="p-0.5 rounded hover:bg-blue-600 text-gray-300 hover:text-white"
                                  title={t('jobs.editJob')}
                                >
                                  <Edit className="h-3 w-3 stroke-[2.5]" />
                                </button>
                                )}
                                
                                {canDeleteJobs && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteJob(job);
                                  }}
                                  className="p-0.5 rounded hover:bg-red-600 text-gray-300 hover:text-white"
                                  title={t('jobs.deleteJob')}
                                >
                                  <Trash2 className="h-3 w-3 stroke-[2.5]" />
                                </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {expandedJobId === job._id && (
                          <JobQueueExpandedDetails job={job} {...expandedDetailsProps} />
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}

              {isForwardedVariant && visibleOutgoingHandoffJobs.length === 0 && (
                <p className="text-xs text-gray-400 py-3 text-center">
                  {outgoingHandoffJobs.length === 0
                    ? t('jobs.noForwardedJobs')
                    : t('jobs.noForwardedJobsMatch')}
                </p>
              )}

              {isForwardedVariant && visibleOutgoingHandoffJobs.map((job, outgoingIndex) => {
                const isReturnFlow = !!job.handoffIsReturn;
                const isPartnerDeleted = !!job.handoffDeletedByPartner;
                const isAtPartner = isOutgoingHandoffSourceView(job, currentCompanyId);
                const partnerAssignee = resolveOutgoingHandoffPartnerAssignee(
                  job,
                  partners,
                  currentCompanyId
                );
                const canEditOutgoing =
                  canEditJobs && !isPartnerDeleted && !isOutgoingHandoffEditLocked(job, currentCompanyId);
                const canDeleteOutgoing =
                  canDeleteJobs &&
                  !isPartnerDeleted &&
                  !isAtPartner &&
                  job.handoffStatus === 'pending_acceptance';
                return (
                <Draggable
                  key={job._id}
                  draggableId={job._id}
                  index={outgoingIndex}
                  isDragDisabled={true}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      onClick={() => handleJobClick(job)}
                      className={`bg-gray-700 rounded-lg border-l-4 cursor-pointer ${
                        isPartnerDeleted ? 'opacity-60' : ''
                      } ${
                        selectedJobId === job._id
                          ? 'border-blue-500 bg-gray-600'
                          : isReturnFlow
                            ? 'border-rose-500'
                            : getHandoffBorderClass(job)
                      } ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                    >
                      <div className="p-1.5 sm:p-2">
                        <div className="flex items-center justify-between gap-1.5 sm:gap-2">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                            <span className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded whitespace-nowrap ${getStatusColor(job.status)}`}>
                              {job.status.substring(0, 3)}
                            </span>

                            {renderHandoffPhaseBadge(job)}

                            <span className={`text-[9px] sm:text-[10px] font-medium whitespace-nowrap ${getPriorityColor(job.priority)}`}>
                              {job.priority.substring(0, 1)}
                            </span>

                            <span className="text-[10px] sm:text-xs font-medium text-white truncate flex-1 min-w-0">
                              {handoffRowPrimaryLabel(job)}
                            </span>

                            <span onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-shrink-0 ml-auto">
                              {partnerAssignee && (
                                <span
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                                  style={{
                                    backgroundColor: getAssigneeColor(partnerAssignee, workers, partners),
                                    background: getAssigneeColor(partnerAssignee, workers, partners)
                                  }}
                                  title={getWorkerName(partnerAssignee, workers, partners)}
                                >
                                  {getWorkerInitials(partnerAssignee, workers, partners)}
                                </span>
                              )}
                              <DeadlineMarker job={job} size="sm" />
                            </span>

                            <span className="hidden sm:inline text-[9px] sm:text-[10px] text-gray-300 truncate max-w-[120px]">
                              {formatDisplayValue(job.clientName)}
                            </span>
                          </div>

                          <div className="flex items-center gap-0.5 sm:gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(job._id);
                              }}
                              className="p-0.5 rounded hover:bg-gray-600 text-gray-300 hover:text-white"
                              title={t('common.details')}
                            >
                              {expandedJobId === job._id ? (
                                <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              ) : (
                                <List className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              )}
                            </button>

                            {canEditOutgoing && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingJob(job);
                                }}
                                className="p-0.5 rounded hover:bg-gray-600 text-blue-400 hover:text-blue-300"
                                title="Edit Job"
                              >
                                <Edit className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                            )}

                            {canDeleteOutgoing && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteJob(job);
                                }}
                                className="p-0.5 rounded hover:bg-gray-600 text-red-400 hover:text-red-300"
                                title="Delete Job"
                              >
                                <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {expandedJobId === job._id && (
                          <JobQueueExpandedDetails job={job} {...expandedDetailsProps} />
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              );})}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}

      {!isForwardedVariant && !loading && filteredJobs.length === 0 && (
        <div className="text-center text-gray-400 py-2 sm:py-8">
          {jobs && jobs.length === 0 ? (
            <div>
              <p className="text-sm sm:text-lg font-medium mb-1 sm:mb-2">{t('jobs.noJobsFound')}</p>
              <p className="text-xs sm:text-sm">{t('jobs.createJob')}</p>
            </div>
          ) : (
            <div>
              <p className="text-sm sm:text-lg font-medium mb-1 sm:mb-2">{t('jobs.noJobsMatch')}</p>
              {searchTerm.trim() && (
                <p className="text-xs sm:text-sm">{t('jobs.tryAdjustingFilters')}</p>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Edit Job Modal - Mobile Responsive */}
      {editingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg p-3 sm:p-6 max-w-4xl w-full max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-800 pb-2 border-b border-gray-700">
              <h2 className="text-base sm:text-xl font-bold text-white">{t('jobs.editJob')}</h2>
              <button
                onClick={() => setEditingJob(null)}
                className="text-gray-400 hover:text-white text-xl sm:text-2xl"
              >
                ✕
              </button>
            </div>
            <JobForm
              isEdit={true}
              jobData={editingJob}
              onClose={() => setEditingJob(null)}
            />
          </div>
        </div>
      )}
      
      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-white">{t('jobs.jobDetails')}</h3>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Client Information */}
                <div className="bg-gray-700 rounded p-4">
                  <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {t('jobs.clientInfo')}
                  </h4>
                  <div className="text-sm text-gray-300 space-y-2">
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.name')}:</span>
                      <span className="flex-1 font-medium">{formatDisplayValue(selectedJob.clientName)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.phone')}:</span>
                      <span className="flex-1">{filterPlaceholder(selectedJob.clientPhone)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.email')}:</span>
                      <span className="flex-1">
                        {selectedJob.clientEmail && selectedJob.clientEmail !== 'no-email@example.com' && selectedJob.clientEmail.trim()
                          ? selectedJob.clientEmail
                          : ''}
                      </span>
                    </div>
                    {selectedJob.clientAddress && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">{t('jobs.address')}:</span>
                        <span className="flex-1">{selectedJob.clientAddress}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Service Details */}
                <div className="bg-gray-700 rounded p-4">
                  <h4 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t('jobs.serviceDetails')}
                  </h4>
                  <div className="text-sm text-gray-300 space-y-2">
                    {selectedJob.serviceDate && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">{t('jobs.date')}:</span>
                        <span className="flex-1">{new Date(selectedJob.serviceDate).toLocaleDateString('sr-RS')}</span>
                      </div>
                    )}
                    {selectedJob.scheduledTime && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">{t('jobs.time')}:</span>
                        <span className="flex-1">{selectedJob.scheduledTime}</span>
                      </div>
                    )}
                    {selectedJob.estimatedDuration && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">{t('jobs.duration')}:</span>
                        <span className="flex-1 text-blue-400 font-medium">{selectedJob.estimatedDuration}h</span>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.status')}:</span>
                      <span className="flex-1">{getDisplayStatus(selectedJob)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.priority')}:</span>
                      <span className="flex-1">{selectedJob.priority}</span>
                    </div>
                    {filterPlaceholder(selectedJob.assignedTo) && (
                      <div className="flex gap-2 items-center">
                        <span className="text-gray-400 min-w-[100px]">{t('jobs.assignedTo')}:</span>
                        <div className="flex-1 flex items-center gap-2">
                          <div 
                            className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 shadow-sm"
                            style={{ backgroundColor: getWorkerColor(selectedJob.assignedTo, workers), background: getWorkerColor(selectedJob.assignedTo, workers) }}
                            title={getWorkerName(selectedJob.assignedTo, workers, partners)}
                          >
                            {getWorkerInitials(selectedJob.assignedTo, workers, partners)}
                          </div>
                          <span>{getWorkerName(selectedJob.assignedTo, workers, partners)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Issue Description */}
                {filterPlaceholder(selectedJob.issueDescription) && (
                  <div className="bg-gray-700 rounded p-4">
                    <h4 className="font-semibold text-white text-sm mb-3">{t('jobs.issueDescription')}</h4>
                    <div className="space-y-2 text-sm text-gray-300">
                      {(selectedJob.deviceType || selectedJob.deviceBrand || selectedJob.deviceModel || selectedJob.deviceSerialNumber) && (
                        <>
                          <p className="text-gray-400 font-semibold">Podaci o aparatu:</p>
                          {selectedJob.deviceType && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceType')}:</span>
                              <span className="text-white">{filterPlaceholder(selectedJob.deviceType)}</span>
                            </div>
                          )}
                          {selectedJob.deviceBrand && filterPlaceholder(selectedJob.deviceBrand) && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceBrand')}:</span>
                              <span className="text-white">{selectedJob.deviceBrand}</span>
                            </div>
                          )}
                          {selectedJob.deviceModel && filterPlaceholder(selectedJob.deviceModel) && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceModel')}:</span>
                              <span className="text-white">{selectedJob.deviceModel}</span>
                            </div>
                          )}
                          {formatDisplayValue(selectedJob.deviceSerialNumber) !== '-' && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceSerialNumber')}:</span>
                              <span className="text-white">{formatDisplayValue(selectedJob.deviceSerialNumber)}</span>
                            </div>
                          )}
                        </>
                      )}
                      <div>
                        <p className="text-gray-400 font-semibold mb-1">Opis problema:</p>
                        <p className="text-white whitespace-pre-wrap">{selectedJob.issueDescription}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Edit/Delete — company users or workers with matching permissions */}
                {(canEditJobs || canDeleteJobs) && (
                  <div className="flex gap-3 pt-4 border-t border-gray-600">
                    {canEditJobs && (
                    <button
                      onClick={() => {
                        setEditingJob(selectedJob);
                        setSelectedJob(null);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                      {t('jobs.editJob')}
                    </button>
                    )}
                    {canDeleteJobs && (
                    <button
                      onClick={() => handleDeleteJob(selectedJob)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('jobs.deleteJob')}
                    </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {imagePreviewJob && Array.isArray(imagePreviewJob.deviceImages) && imagePreviewJob.deviceImages.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
              <h3 className="text-sm font-semibold text-white">
                Slike aparata – {formatDisplayValue(imagePreviewJob.deviceType) || 'nedefinisano'}
              </h3>
              <button
                type="button"
                onClick={() => setImagePreviewJob(null)}
                className="text-gray-400 hover:text-white text-sm"
              >
                Zatvori
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <div className="flex flex-wrap gap-3 justify-start">
                {imagePreviewJob.deviceImages.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => window.open(imgUrl, '_blank')}
                    className="relative border border-gray-700 rounded-md overflow-hidden bg-black/40 w-20 h-20 sm:w-24 sm:h-24"
                  >
                    <img
                      src={imgUrl}
                      alt={`Aparat ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobQueue;
